import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Container, Theme } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makeStyles } from '@material-ui/styles';

import { getFileURL } from '../../../utils/common';
import { ROUTE_INFO, ROUTE_NEWS_MANAGEMENT } from '../index';
import PageContainer from '../../../components/PageContainer';
import Loader from '../../../components/Loader';
import NewsRepository from './NewsRepository';
import NewsForm from './NewsForm';
import { NewsItem } from '../../../types';
import AppContext from '../../../AppContext';
import { NewsItemProps } from './NewsItem';

type Filter = {
  afasId: number;
  moodleCourseId?: number | null;
  name: string;
  participantAfasId?: number | null;
  practicalTrainerAfasId?: number | null;
  startDate?: string | null;
};
interface Filters {
  events: Filter[];
  educations: Filter[];
  roles: [];
}

const useStyles = makeStyles((theme: Theme) => ({
  buttonRemoveImage: {
    marginRight: theme.spacing(1),
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    background: theme.palette.common.white,
    borderRadius: 4,

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(5),
      paddingRight: 120,
      paddingLeft: 120,
    },
  },
  containerHasImage: {
    marginTop: -100,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    background: theme.palette.common.white,
    borderRadius: 4,

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(5),
      paddingRight: 120,
      paddingLeft: 120,
    },
  },
  visual: {
    height: 300,
    width: '100%',
    zIndex: 1,
    position: 'relative',
    objectFit: 'cover',

    [theme.breakpoints.up('md')]: {
      height: 500,
    },
  },
}));

const NewsEditView = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const notifications = useSnackbar();
  const classes = useStyles();
  const { roleViewManager } = useContext(AppContext);

  const [image, setFile] = useState<File>();
  const [files, setFiles] = useState<File[]>();
  const [content, setContent] = useState<NewsItem | null>(null);
  const [filters, setFilters] = useState<Filters | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const news = new NewsRepository();

  /**
   * Fetch data from backend.
   */
  const fetch = async () => {
    setLoading(true);

    await news
      .getFilters()
      .then(({ data }) => {
        setFilters(data);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Er ging iets mis bij het ophalen van de filters',
          {
            variant: 'error',
          },
        );
      });

    await news
      .find(id)
      .then(({ data }) => {
        setContent(data);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Er ging iets mis bij het ophalen van de bericht data',
          {
            variant: 'error',
          },
        );
      });

    setLoading(false);
  };

  /**
   * Handle image delete.
   */
  const onDeleteImage = () => {
    if (!content?.image) return;

    news
      .deleteImage(content.id)
      .then(() => {
        notifications.enqueueSnackbar('Afbeelding verwijderd', {
          variant: 'success',
        });
        history.push(`${ROUTE_INFO}`);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Er ging iets mis, de afbeelding kon niet worden verwijderd',
          {
            variant: 'error',
          },
        );
      });
  };

  /**
   * News actions.
   */
  const newsActions = (
    <div>
      {roleViewManager.hasRole(['ROLE_ADMIN', 'ROLE_PROGRAM_ASSISTANT']) && (
        <>
          {content?.image && (
            <Button
              color="primary"
              startIcon={<FontAwesomeIcon icon={['fal', 'image']} />}
              onClick={onDeleteImage}
              className={classes.buttonRemoveImage}
            >
              Afbeelding verwijderen
            </Button>
          )}
        </>
      )}
    </div>
  );

  useEffect(() => {
    fetch();
  }, []);

  /**
   * Handle file change.
   */
  const handleFileChange = (file: File) => {
    setFile(file);
  };

  /**
   * Handle files change.
   */
  const handleFilesChange = (files: File[]) => {
    setFiles(files);
  };

  /**
   * Handle file upload.
   */
  const handleImageUpload = (id: string, file: File | undefined) => {
    if (!file) {
      return null;
    }

    const data = new FormData();
    data.append('image', file);

    return news.uploadImage(id, data).catch(() => {
      notifications.enqueueSnackbar(
        'Er ging iets mis bij het uploaden van de afbeelding naar het nieuwsbericht',
        {
          variant: 'error',
        },
      );
    });
  };

  /**
   * Handle file upload.
   */
  const handleFilesUpload = (id: string, files: File[] | undefined) => {
    if (!files || files.length === 0) {
      return null;
    }

    const data = new FormData();

    files.forEach((file, index) => {
      data.append(`files[${index}]`, files[index]);
    });

    return news.uploadFiles(id, data).catch(() => {
      notifications.enqueueSnackbar(
        'Er ging iets mis bij het uploaden van de bestanden',
        {
          variant: 'error',
        },
      );
    });
  };

  /**
   * Handle form submit.
   */
  const handleSubmit = (data: NewsItemProps) => {
    news
      .update(id, data)
      .then(({ data }) => {
        notifications.enqueueSnackbar('Succesvol opgeslagen/gepubliceerd', {
          variant: 'success',
        });

        const imageUpload = handleImageUpload(data.id, image);
        const filesUpload = handleFilesUpload(data.id, files);

        return Promise.all([imageUpload, filesUpload]);
      })
      .then(() => {
        history.push(`${ROUTE_NEWS_MANAGEMENT}`);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Er ging iets mis bij het aanmaken van het nieuwsbericht',
          {
            variant: 'error',
          },
        );
      });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Container maxWidth="xl">
      {content?.image && (
        <img
          className={classes.visual}
          src={getFileURL(content?.image)}
          alt=""
        />
      )}
      <PageContainer
        containerClassName={`${
          content?.image ? classes.containerHasImage : classes.container
        }`}
        actions={newsActions}
        marginBottom
        title="Nieuws bewerken"
      >
        <NewsForm
          item={content}
          filters={filters}
          onChangeFile={handleFileChange}
          onUploadFiles={handleFilesChange}
          onSubmit={handleSubmit}
        />
      </PageContainer>
    </Container>
  );
};

export default NewsEditView;
